// pages/EventInvitation.js
//
// The web invitation. Most guests open an invite link in a browser before they
// ever install the app, so this page mirrors the app's event detail screen
// (invits-app-new/src/features/events/screens/EventDetailScreen.js) section for
// section: a full-bleed themed background, a floating header that resolves into
// a bar on scroll, inline sections separated by hairlines rather than cards,
// and the RSVP / host / paid actions pinned to the bottom.
//
// Access and RSVP decisions come from shared/ (eventAccess, rsvpGate, eventCta)
// so the web and the app offer the same action in the same situation.
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  IoArrowBack, IoShareOutline, IoCalendarOutline, IoLocationOutline, IoPricetag,
  IoEyeOffOutline, IoPeopleOutline, IoAlertCircle, IoAlertCircleOutline, IoStar,
  IoShieldCheckmark, IoChevronForward, IoLockClosed, IoChatbubblesOutline,
  IoLanguageOutline, IoMegaphoneOutline, IoImages, IoNavigate, IoMusicalNotes,
  IoMusicalNote, IoLogoYoutube, IoCallOutline,
  IoShieldCheckmarkOutline, IoArrowForward, IoPlayCircle, IoDocumentText, IoPlay,
  IoAlbumsOutline, IoTimeOutline, IoCloseCircleOutline, IoKey, IoArrowDown,
  IoTicket, IoCash, IoCheckmarkCircleOutline, IoWalletOutline, IoPersonOutline,
  IoMailOutline, IoOpenOutline, IoBusinessOutline, IoPeople, IoList, IoInformationCircle, IoInformationCircleOutline, IoCalendar, IoClose,
  IoCheckmark, IoTime, IoGameController,
  IoCheckmarkCircle, IoHelpCircle, IoCloseCircle, IoDownloadOutline,
  IoDocumentTextOutline, IoPhonePortraitOutline, IoCopyOutline,
} from 'react-icons/io5';
import supabase from '../services/supabase';
import { useAuth } from '../services/auth';
import RequestAccessModal from '../components/RequestAccessModal';
import PassPurchaseModal from '../components/PassPurchaseModal';
import GuestlistRsvpModal from '../components/GuestlistRsvpModal';
import MapPreview from '../components/MapPreview';
import UserAvatar from '../components/UserAvatar.jsx';
import DateTimePollModal from '../components/DateTimePollModal';
import SongRequestModal from '../components/SongRequestModal';
import SongQueueModal from '../components/SongQueueModal';
import AdditionalQuestionsModal from '../components/AdditionalQuestionsModal';
import PlusOnesModal from '../components/PlusOnesModal';
import { getFontById } from '../constants/cardFonts';
import { resolveEventAccess, verdictToGateAccess } from '../shared/eventAccess';
import { resolveRsvpGate } from '../shared/rsvpGate';
import { resolveEventCta, ctaReplacesRsvpButtons } from '../shared/eventCta';
import { invitationTextColor, hexToRgb, luminance } from '../shared/eventPalette';
import { parseLocalDate, combineDateAndTime, endOfDay } from '../shared/eventDateTime';
import '../styles/EventInvitation.scss';
import '../styles/EventDetail.scss';
import '../styles/MapPreview.scss';
import '../styles/DateTimePollModal.scss';
import '../styles/SongRequestModal.scss';
import '../styles/SongQueueModal.scss';
import '../styles/AdditionalQuestionsModal.scss';

import { inviteUrl } from '../shared/shortLinks';
import { effectPlayerHtml, effectUrlFor } from '../shared/effectPlayer';
/** How far the page scrolls before the header is fully a bar. Matches the app. */
const HEADER_FADE_DISTANCE = 72;

/** How far past the title the header title crossfade completes. Matches the app. */
const TITLE_FADE_DISTANCE = 60;

const DEFAULT_EMOJIS = { going: '✅', maybe: '🤔', notGoing: '❌' };

const clamp01 = (n) => Math.max(0, Math.min(1, n));

/** Playlist id from a Spotify URI or URL, as the app extracts it. */
const spotifyPlaylistId = (playlist) => {
  if (typeof playlist !== 'string' || !playlist) return null;
  if (playlist.startsWith('spotify:playlist:')) return playlist.split(':').pop();
  if (playlist.includes('open.spotify.com/playlist/')) return playlist.split('/').pop().split('?')[0];
  return null;
};

/** Playlist id from the JSONB object or a legacy URL / id string. */
const youtubePlaylistId = (playlist) => {
  if (!playlist) return null;
  if (typeof playlist === 'object') return playlist.id || null;
  if (typeof playlist === 'string') {
    return playlist.includes('list=') ? playlist.split('list=')[1]?.split('&')[0] : playlist;
  }
  return null;
};

const EventInvitation = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [event, setEvent] = useState(null);

  // The invitation's effect (bubbles, snowfall…): a transparent video over
  // the page. Looked up only when the invitation has one.
  const [effect, setEffect] = useState(null);
  useEffect(() => {
    if (!event?.effect_id) { setEffect(null); return undefined; }
    let cancelled = false;
    supabase
      .from('invitation_effects')
      .select('id, mobile_url, web_url')
      .eq('id', event.effect_id)
      .maybeSingle()
      .then(({ data }) => { if (!cancelled) setEffect(data ?? null); });
    return () => { cancelled = true; };
  }, [event?.effect_id]);
  // Chosen once per effect: the portrait file for a portrait window.
  const effectHtml = useMemo(() => {
    const url = effectUrlFor(effect, { width: window.innerWidth, height: window.innerHeight });
    return url ? effectPlayerHtml(url) : null;
  }, [effect]);
  const [loading, setLoading] = useState(true);
  const [isUserHost, setIsUserHost] = useState(false);
  const [isUserCohost, setIsUserCohost] = useState(false);
  const [userRsvpStatus, setUserRsvpStatus] = useState(null);
  const [rsvpAttendees, setRsvpAttendees] = useState([]);
  const [hostProfiles, setHostProfiles] = useState([]);
  const [linkedCommunity, setLinkedCommunity] = useState(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [showPdfGallery, setShowPdfGallery] = useState(false);
  const [pdfCurrentPage, setPdfCurrentPage] = useState(0);
  const [playingYouTube, setPlayingYouTube] = useState(false);

  // Access
  const [onGuestList, setOnGuestList] = useState(false);
  const [accessRequest, setAccessRequest] = useState(null);
  const [accessResolved, setAccessResolved] = useState(false);

  // Modals
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showPassModal, setShowPassModal] = useState(false);
  const [showGoingModal, setShowGoingModal] = useState(false);
  const [showRsvpSignup, setShowRsvpSignup] = useState(false);
  const [pendingRsvpOnAuth, setPendingRsvpOnAuth] = useState(null);
  const [showAppDownloadModal, setShowAppDownloadModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [appDownloadAction, setAppDownloadAction] = useState('');
  const [copied, setCopied] = useState(false);

  // Date/time poll
  const [showDateTimePollModal, setShowDateTimePollModal] = useState(false);
  const [pollResults, setPollResults] = useState(null);
  const [pendingRsvpStatus, setPendingRsvpStatus] = useState(null);
  const [isVotingOnPoll, setIsVotingOnPoll] = useState(false);

  // Song requests
  const [showSongRequestModal, setShowSongRequestModal] = useState(false);
  const [showSongQueueModal, setShowSongQueueModal] = useState(false);

  // RSVP follow-ups
  const [showAdditionalQuestionsModal, setShowAdditionalQuestionsModal] = useState(false);
  const [showPlusOnesModal, setShowPlusOnesModal] = useState(false);
  const [pendingPlusOneNames, setPendingPlusOneNames] = useState([]);
  const [pendingAuthUserId, setPendingAuthUserId] = useState(null);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [rsvpFailed, setRsvpFailed] = useState(false);
  // A ref, not state: read and set synchronously in the tap handler, so rapid
  // repeat taps cannot each start a write.
  const rsvpInFlightRef = useRef(false);

  const [userPurchase, setUserPurchase] = useState(null);
  const [pendingPaymentCount, setPendingPaymentCount] = useState(0);
  const [onWaitlist, setOnWaitlist] = useState(false);
  const [emojiConfig, setEmojiConfig] = useState(DEFAULT_EMOJIS);

  // Layout
  const [scrollY, setScrollY] = useState(0);
  const [titleOffset, setTitleOffset] = useState(null);
  const [columnWidth, setColumnWidth] = useState(null);
  const [coverRatio, setCoverRatio] = useState(null);
  const columnRef = useRef(null);
  const titleRef = useRef(null);
  const carouselRef = useRef(null);

  const isPaidEvent = useCallback((ev = event) =>
    ['paid', 'fixed', 'variable'].includes(ev?.pass_type) &&
    Array.isArray(ev?.passes) && ev.passes.length > 0, [event]);
  const isContributionEvent = event?.pass_type === 'contribution';
  const isHostOrCohost = isUserHost || isUserCohost;

  useEffect(() => {
    if (!authLoading && !event) fetchEventData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, authLoading]);

  // User-specific data when the signed-in user changes.
  useEffect(() => {
    if (!authLoading && user && event) {
      checkUserAccess(event, user.id);
      fetchUserRsvp(event.id, user.id);
      fetchRsvps(event);
      loadUserPurchase(event, user.id);
      loadWaitlistStatus(event, user.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, authLoading]);

  // Header crossfade and bar, driven by the window scroll.
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // The column stands in for the app's screen width in the cover sizing rules.
  useEffect(() => {
    const el = columnRef.current;
    if (!el) return undefined;
    const measure = () => {
      setColumnWidth(el.clientWidth);
      if (titleRef.current) {
        setTitleOffset(titleRef.current.getBoundingClientRect().top + window.scrollY - 60);
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [event, coverRatio, loading]);

  // Cover proportions come from the image itself, as in the app.
  useEffect(() => {
    const url = event?.cover_image_cdn_url || event?.cover_image_direct_url;
    if (!url) return;
    const img = new Image();
    img.onload = () => setCoverRatio(img.width / img.height);
    img.src = url;
  }, [event?.cover_image_cdn_url, event?.cover_image_direct_url]);

  const fetchEventData = async () => {
    try {
      setLoading(true);

      const { data: slugData, error: slugError } = await supabase
        .from('event_slugs')
        .select('event_id, financial_year')
        .eq('slug', slug)
        .single();
      if (slugError) throw slugError;

      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select(`
          *,
          host:users!host_id(
            id,
            username,
            first_name,
            last_name,
            email,
            profile_photo_cdn_url,
            avatar_config
          )
        `)
        .eq('id', slugData.event_id)
        .eq('financial_year', slugData.financial_year)
        .single();
      if (eventError) throw eventError;

      if (!eventData.passes || eventData.passes.length === 0) {
        const { data: passesData } = await supabase
          .from('event_passes')
          .select('*')
          .eq('event_id', slugData.event_id)
          .eq('financial_year', slugData.financial_year);
        if (passesData) eventData.passes = passesData;
      }

      setEvent(eventData);

      if (eventData.emoji_set === 'custom' && eventData.custom_emojis) {
        setEmojiConfig({ ...DEFAULT_EMOJIS, ...eventData.custom_emojis });
      }

      let hostStatus = { isHost: false, isCohost: false };
      if (user) {
        hostStatus = await checkUserAccess(eventData, user.id);
        await fetchUserRsvp(eventData.id, user.id);
        await fetchRsvps(eventData);
        await loadUserPurchase(eventData, user.id);
        await loadWaitlistStatus(eventData, user.id);
      } else {
        setAccessResolved(true);
      }

      await fetchHostProfiles(eventData);
      if (eventData.community_id) await fetchCommunity(eventData.community_id);
      await loadDateTimePollData(eventData);
      await loadPendingPaymentCount(eventData, hostStatus.isHost || hostStatus.isCohost);
    } catch (error) {
      console.error('Error fetching event:', error);
    } finally {
      setLoading(false);
    }
  };

  /** Resolves host, co-host, guest list and approval facts for one user. */
  const checkUserAccess = async (eventData, userId) => {
    const isHost = eventData.host_id === userId;
    setIsUserHost(isHost);
    let isCohost = false;
    let inGuestList = false;
    let request = null;

    try {
      const { data: cohostData } = await supabase
        .from('event_cohosts')
        .select('user_id')
        .eq('event_id', eventData.id)
        .eq('financial_year', eventData.financial_year)
        .eq('user_id', userId)
        .eq('status', 'accepted')
        .maybeSingle();
      isCohost = !!cohostData;
      setIsUserCohost(isCohost);

      if (!isHost && !isCohost && eventData.privacy === 'private') {
        inGuestList = await checkGuestListAccess(eventData, userId);
      }
      if (!isHost && !isCohost && eventData.privacy !== 'public') {
        request = await fetchAccessRequest(eventData, userId);
      }
    } catch (error) {
      console.error('Error in checkUserAccess:', error);
    } finally {
      setOnGuestList(inGuestList);
      setAccessRequest(request);
      setAccessResolved(true);
    }

    return {
      isHost,
      isCohost,
      verdict: resolveEventAccess({
        isHost,
        isCohost,
        privacy: eventData.privacy,
        onGuestList: inGuestList,
        approvalStatus: request?.status ?? null,
      }),
    };
  };

  /** Same check as the app: the user's phone against the event's guest lists. */
  const checkGuestListAccess = async (eventData, userId) => {
    const { data: profile } = await supabase
      .from('users')
      .select('phone_number')
      .eq('id', userId)
      .single();
    if (!profile?.phone_number) return false;

    const { data, error } = await supabase.rpc('check_user_in_event_guest_lists', {
      p_event_id: eventData.id,
      p_financial_year: eventData.financial_year,
      p_user_phone: profile.phone_number,
    });
    if (error) {
      console.error('Guest list check failed:', error);
      return false;
    }
    return !!data?.[0]?.in_guest_list;
  };

  const fetchAccessRequest = async (eventData, userId) => {
    const { data, error } = await supabase
      .from('event_access_requests')
      .select('*')
      .eq('event_id', eventData.id)
      .eq('financial_year', eventData.financial_year)
      .eq('user_id', userId)
      .maybeSingle();
    if (error && error.code !== 'PGRST116') throw error;
    return data;
  };

  const fetchUserRsvp = async (eventId, userId) => {
    const { data } = await supabase
      .from('event_rsvps')
      .select('status')
      .eq('event_id', eventId)
      .eq('user_id', userId)
      .maybeSingle();
    setUserRsvpStatus(data?.status ?? null);
  };

  const fetchRsvps = async (eventData) => {
    let query = supabase
      .from('event_rsvps')
      .select(`
        *,
        user:users!event_rsvps_user_id_fkey(
          id,
          username,
          first_name,
          last_name,
          profile_photo_cdn_url
        )
      `)
      .eq('event_id', eventData.id)
      .eq('status', 'going')
      .order('created_at', { ascending: false });
    if (eventData.financial_year) query = query.eq('financial_year', eventData.financial_year);

    const { data, error } = await query;
    if (error) {
      console.error('[RSVPS] fetchRsvps error:', error);
      return;
    }
    if (data) setRsvpAttendees(data.filter((r) => r.user));
  };

  const fetchHostProfiles = async (eventData) => {
    const profiles = eventData.host ? [{ ...eventData.host, isMainHost: true }] : [];

    const { data: cohosts } = await supabase
      .from('event_cohosts')
      .select(`
        user:users!fk_event_cohosts_user(
          id,
          username,
          first_name,
          last_name,
          profile_photo_cdn_url,
          avatar_config
        )
      `)
      .eq('event_id', eventData.id)
      .eq('financial_year', eventData.financial_year)
      .eq('status', 'accepted');

    (cohosts || []).forEach((cohost) => {
      if (cohost.user) profiles.push({ ...cohost.user, isMainHost: false });
    });
    setHostProfiles(profiles);
  };

  const fetchCommunity = async (communityId) => {
    const { data } = await supabase
      .from('communities')
      .select('id, name, username, cover_image_cdn_url, members_count')
      .eq('id', communityId)
      .single();
    if (data) setLinkedCommunity(data);
  };

  const loadDateTimePollData = async (eventData) => {
    if (!eventData.date_time_poll_enabled) return;
    try {
      const { data, error } = await supabase
        .from('date_time_poll_options')
        .select('*')
        .eq('event_id', eventData.id)
        .eq('financial_year', eventData.financial_year)
        .order('option_order', { ascending: true });
      if (!error && data) setPollResults({ options: data, pollEnabled: true });
    } catch (error) {
      console.error('Error loading date/time poll:', error);
    }
  };

  const loadUserPurchase = async (eventData, userId) => {
    const needsPurchase = isPaidEvent(eventData) || eventData.pass_type === 'contribution';
    if (!userId || !needsPurchase) return;
    const { data } = await supabase
      .from('attendee_pass_purchases')
      .select('*')
      .eq('event_id', eventData.id)
      .eq('financial_year', eventData.financial_year)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    setUserPurchase(data ?? null);
  };

  const loadPendingPaymentCount = async (eventData, isHostUser) => {
    if (!isHostUser) return;
    const { count, error } = await supabase
      .from('attendee_pass_purchases')
      .select('*', { count: 'exact', head: true })
      .eq('event_id', eventData.id)
      .eq('financial_year', eventData.financial_year)
      .eq('verification_status', 'pending');
    if (!error) setPendingPaymentCount(count || 0);
  };

  const loadWaitlistStatus = async (eventData, userId) => {
    if (!userId || !eventData.enable_waiting_list) return;
    const { data } = await supabase
      .from('event_rsvp_waitlist')
      .select('position')
      .eq('event_id', eventData.id)
      .eq('user_id', userId)
      .maybeSingle();
    setOnWaitlist(!!data);
  };

  // ── Access ────────────────────────────────────────────────────────────────

  const accessVerdict = useMemo(() => event && resolveEventAccess({
    isHost: isUserHost,
    isCohost: isUserCohost,
    privacy: event.privacy,
    onGuestList,
    approvalStatus: accessRequest?.status ?? null,
  }), [event, isUserHost, isUserCohost, onGuestList, accessRequest]);

  const canAccessContent = () => !!accessVerdict?.fullAccess;
  const canViewRsvpList = () => userRsvpStatus === 'going' || isHostOrCohost;
  const shouldShowLocation = () => {
    if (isHostOrCohost) return true;
    if (event.hide_location_before_verification) return canAccessContent();
    return true;
  };
  const hasPendingRequest = accessRequest?.status === 'pending' && !canAccessContent();

  const eventCapacity = useMemo(() => {
    if (!event?.max_attendees) return null;
    const spotsRemaining = Math.max(0, event.max_attendees - (event.current_spots || 0));
    return { spotsRemaining, isFull: spotsRemaining <= 0 };
  }, [event?.max_attendees, event?.current_spots]);

  const eventHasEnded = useMemo(() => {
    const end = combineDateAndTime(
      parseLocalDate(event?.end_date ?? event?.event_date),
      event?.end_time ?? event?.event_time,
    );
    return !!end && end.getTime() < Date.now();
  }, [event?.end_date, event?.end_time, event?.event_date, event?.event_time]);

  const rsvpDeadlineHasPassed = useMemo(() => {
    const deadline = parseLocalDate(event?.rsvp_deadline);
    return !!deadline && endOfDay(deadline).getTime() < Date.now();
  }, [event?.rsvp_deadline]);

  const footerCta = useMemo(() => {
    if (!event) return { kind: 'loading' };
    const gateAccess = verdictToGateAccess(accessVerdict);
    let gate;
    if (isHostOrCohost) {
      gate = { kind: 'hidden', reason: 'host' };
    } else if (!user && gateAccess?.fullAccess && !isPaidEvent()) {
      // Signed-out guests on an open invitation see the same three buttons a
      // signed-in guest does; tapping one signs them in and completes the RSVP.
      gate = eventCapacity?.isFull
        ? { kind: 'locked', lockedBy: 'capacity', canJoinWaitlist: !!event.enable_waiting_list }
        : { kind: 'available' };
    } else {
      gate = resolveRsvpGate({
        isAuthed: !!user,
        accessStatus: accessResolved ? 'resolved' : 'pending',
        access: gateAccess,
        event,
        capacity: { isFull: !!eventCapacity?.isFull },
        requiresPayment: isPaidEvent(),
      });
    }
    return resolveEventCta({
      gate,
      rsvpStatus: userRsvpStatus,
      isPast: eventHasEnded,
      deadlinePassed: rsvpDeadlineHasPassed,
      onWaitlist,
      hasDatePoll: !!event.date_time_poll_enabled,
    });
  }, [event, accessVerdict, isHostOrCohost, user, accessResolved, eventCapacity,
    isPaidEvent, userRsvpStatus, eventHasEnded, rsvpDeadlineHasPassed, onWaitlist]);

  // One value decides pill vs inline section, so they are exact complements.
  const rsvpInFooter = !!event
    && !isHostOrCohost
    && !isPaidEvent()
    && !isContributionEvent
    && !event.date_time_poll_enabled
    && footerCta.kind !== 'none'
    && footerCta.kind !== 'loading';

  // ── RSVP ──────────────────────────────────────────────────────────────────

  const handleRsvp = (status) => {
    if (!user) {
      setPendingRsvpOnAuth(status);
      setShowRsvpSignup(true);
      return;
    }
    startRsvpFlow(status, user.id);
  };

  /** Plus-ones, then additional questions, then the date poll, then the write. */
  const startRsvpFlow = (status, userId) => {
    if (event.plus_ones_allowed > 0 && status === 'going') {
      setPendingRsvpStatus(status);
      setPendingAuthUserId(userId);
      setShowPlusOnesModal(true);
      return;
    }
    if (event.additional_info_fields?.length > 0) {
      setPendingRsvpStatus(status);
      setPendingAuthUserId(userId);
      setShowAdditionalQuestionsModal(true);
      return;
    }
    if (event.date_time_poll_enabled && status === 'going') {
      setPendingRsvpStatus(status);
      setShowDateTimePollModal(true);
      return;
    }
    submitRsvp(status, userId);
  };

  const handlePlusOnesSubmit = (names) => {
    setShowPlusOnesModal(false);
    setPendingPlusOneNames(names);
    const status = pendingRsvpStatus || 'going';
    const userId = pendingAuthUserId;

    if (event.additional_info_fields?.length > 0) {
      setShowAdditionalQuestionsModal(true);
      return;
    }
    if (event.date_time_poll_enabled && status === 'going') {
      setShowDateTimePollModal(true);
      return;
    }
    // Names passed directly: the state update above has not landed yet.
    submitRsvp(status, userId, null, names);
    setPendingRsvpStatus(null);
    setPendingAuthUserId(null);
  };

  const handleAdditionalQuestionsSubmit = (answers) => {
    setShowAdditionalQuestionsModal(false);
    submitRsvp(pendingRsvpStatus || 'going', pendingAuthUserId, answers, pendingPlusOneNames);
    setPendingRsvpStatus(null);
    setPendingAuthUserId(null);
  };

  const submitRsvp = async (status, userIdOverride = null, additionalInfoResponses = null, plusOneNames = null) => {
    const activeUserId = userIdOverride || user?.id;
    if (!activeUserId || rsvpInFlightRef.current) return;

    if (!isHostOrCohost && !canAccessContent()) {
      setShowRequestModal(true);
      return;
    }

    if (isPaidEvent() && status === 'going') {
      const { data: purchases } = await supabase
        .from('transactions')
        .select('id')
        .eq('event_id', event.id)
        .eq('user_id', activeUserId)
        .eq('status', 'completed')
        .limit(1);
      if (!purchases || purchases.length === 0) {
        setShowPassModal(true);
        return;
      }
    }

    rsvpInFlightRef.current = true;
    setRsvpLoading(true);
    setRsvpFailed(false);
    try {
      const names = plusOneNames ?? pendingPlusOneNames;
      const rsvpData = {
        event_id: event.id,
        financial_year: event.financial_year,
        user_id: activeUserId,
        status,
        plus_ones: names.length,
        updated_at: new Date().toISOString(),
      };
      if (names.length > 0) rsvpData.plus_one_names = names;
      if (additionalInfoResponses) rsvpData.additional_info_responses = additionalInfoResponses;

      const { error } = await supabase
        .from('event_rsvps')
        .upsert(rsvpData, { onConflict: 'event_id,user_id' });
      if (error) throw error;

      setPendingPlusOneNames([]);
      setUserRsvpStatus(status);
      await fetchRsvps(event);
    } catch (error) {
      console.error('Error updating RSVP:', error);
      setRsvpFailed(true);
    } finally {
      rsvpInFlightRef.current = false;
      setRsvpLoading(false);
    }
  };

  /** GuestlistRsvpModal signed the guest in; finish the RSVP they started. */
  const handleAuthSuccess = async (userId) => {
    setShowRsvpSignup(false);
    const status = pendingRsvpOnAuth;
    setPendingRsvpOnAuth(null);

    const { isHost, isCohost, verdict } = await checkUserAccess(event, userId);
    await fetchUserRsvp(event.id, userId);
    if (!status || isHost || isCohost) return;
    if (!verdict.fullAccess) {
      setShowRequestModal(true);
      return;
    }
    startRsvpFlow(status, userId);
  };

  const handlePollVote = async (selectedOptionIds) => {
    if (!user) return;
    try {
      setIsVotingOnPoll(true);
      await supabase
        .from('date_time_poll_votes')
        .delete()
        .eq('event_id', event.id)
        .eq('financial_year', event.financial_year)
        .eq('user_id', user.id);

      const { error } = await supabase
        .from('date_time_poll_votes')
        .insert(selectedOptionIds.map((optionId) => ({
          event_id: event.id,
          financial_year: event.financial_year,
          option_id: optionId,
          user_id: user.id,
          created_at: new Date().toISOString(),
        })));
      if (error) throw error;

      setShowDateTimePollModal(false);
      await submitRsvp(pendingRsvpStatus || 'going');
      setPendingRsvpStatus(null);
    } catch (error) {
      console.error('Error submitting poll vote:', error);
      alert('Failed to submit your vote. Please try again.');
    } finally {
      setIsVotingOnPoll(false);
    }
  };

  const handleSongRequest = async (requestData) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('song_requests')
        .insert({
          event_id: event.id,
          financial_year: event.financial_year,
          user_id: user.id,
          video_id: requestData.videoId,
          title: requestData.title,
          channel_title: requestData.channelTitle,
          thumbnail: requestData.thumbnail,
          message: requestData.message,
          status: 'pending',
          created_at: new Date().toISOString(),
        });
      if (error) throw error;
      setShowSongRequestModal(false);
    } catch (error) {
      console.error('Error submitting song request:', error);
      alert('Failed to submit song request. Please try again.');
    }
  };

  const requestAccess = () => {
    if (!user) {
      setShowRsvpSignup(true);
      return;
    }
    setShowRequestModal(true);
  };

  /** Routes a pinned-footer CTA to the thing that resolves it, as the app does. */
  const handleFooterCta = (kind) => {
    switch (kind) {
      case 'signIn': return setShowRsvpSignup(true);
      case 'requestAccess':
      case 'requestGuestList': return requestAccess();
      case 'joinWaitlist': return openAppOnly('the waitlist');
      case 'buyPasses': return setShowPassModal(true);
      case 'poll': return handleRsvp('going');
      case 'retry': return fetchEventData();
      default: return undefined;
    }
  };

  const openAppOnly = (featureName) => {
    setAppDownloadAction(featureName);
    setShowAppDownloadModal(true);
  };

  const shareUrl = inviteUrl(slug, window.location.origin);

  const handleCopyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const openDirections = () => {
    const destination = event.location_address
      ? `${event.location_name}, ${event.location_address}`
      : event.location_name || `${event.location_lat},${event.location_lng}`;
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  // ── Formatting ────────────────────────────────────────────────────────────

  const formatDate = (dateString) => {
    if (!dateString) return 'Date not set';
    return new Date(dateString).toLocaleDateString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const time = new Date();
    time.setHours(parseInt(hours, 10), parseInt(minutes, 10));
    return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateTime = () =>
    `${formatDate(event.event_date)}, ${formatTime(event.event_time)}${event.end_time ? ` - ${formatTime(event.end_time)}` : ''}`;

  const formatPollOption = (option) => {
    const dateValue = option.date || option.option_date;
    const timeValue = option.time || option.option_time;
    const date = dateValue
      ? new Date(dateValue).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
      : '';
    return timeValue ? `${date}, ${formatTime(timeValue)}` : date;
  };

  const getLowestPassPrice = () => {
    const prices = (event.passes || []).map((p) => parseFloat(p.price)).filter((p) => p > 0);
    return prices.length > 0 ? Math.min(...prices) : null;
  };

  // ── Media ─────────────────────────────────────────────────────────────────

  const getMediaItems = () => {
    if (Array.isArray(event?.event_media_items) && event.event_media_items.length > 0) {
      return [...event.event_media_items].sort((a, b) => a.order - b.order);
    }
    switch (event?.cover_image_type) {
      case 'gif':
        return (event.gif_cover_cdn_url || event.static_gif_cdn_url) ? [{
          type: 'gif',
          gif: { cdnUrl: event.gif_cover_cdn_url, b2Url: event.gif_cover_direct_url },
          staticGif: { cdnUrl: event.static_gif_cdn_url, b2Url: event.static_gif_direct_url },
        }] : [];
      case 'pdf':
        return event.pdf_page_images?.length > 0 ? [{ type: 'pdf', pdfPages: event.pdf_page_images }] : [];
      case 'youtube':
        return (event.youtube_thumbnail_cdn_url || event.youtube_video_url) ? [{
          type: 'youtube',
          thumbnail: { cdnUrl: event.youtube_thumbnail_cdn_url, b2Url: event.youtube_thumbnail_direct_url },
          youtubeVideoId: event.youtube_video_id,
        }] : [];
      default:
        return (event?.cover_image_cdn_url || event?.cover_image_direct_url) ? [{
          type: 'image',
          cover: { cdnUrl: event.cover_image_cdn_url, b2Url: event.cover_image_direct_url },
        }] : [];
    }
  };

  const getMediaDisplayUri = (item) => {
    switch (item?.type) {
      case 'image': return item.cover?.cdnUrl || item.cover?.b2Url;
      case 'gif': return item.gif?.cdnUrl || item.gif?.b2Url || item.staticGif?.cdnUrl || item.staticGif?.b2Url;
      case 'pdf': return item.pdfPages?.[0]?.cdn_url;
      case 'youtube': return item.thumbnail?.cdnUrl || item.thumbnail?.b2Url;
      default: return null;
    }
  };

  const handleCarouselScroll = (e) => {
    const { scrollLeft, clientWidth } = e.currentTarget;
    if (clientWidth) setCurrentMediaIndex(Math.round(scrollLeft / clientWidth));
  };

  const getBackgroundImageUrl = () => {
    if (event.cover_image_type === 'gif') return event.gif_cover_cdn_url || event.gif_cover_direct_url;
    if (event.cover_image_type === 'pdf') return event.pdf_page_images?.[0]?.cdn_url;
    if (event.cover_image_type === 'youtube') return event.youtube_thumbnail_cdn_url || event.youtube_thumbnail_direct_url;
    return event.cover_image_cdn_url || event.cover_image_direct_url || '/images/events/1.jpg';
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading || authLoading) {
    return (
      <div className="ed ed--loading">
        <div className="ed-column">
          <div className="ed-header-content">
            <span className="ed-skeleton" style={{ width: 40, height: 40, borderRadius: 20 }} />
            <span className="ed-skeleton" style={{ width: 40, height: 40, borderRadius: 20 }} />
          </div>
          <div className="ed-skeleton-stack">
            <span className="ed-skeleton" style={{ width: '100%', height: 340, borderRadius: 16 }} />
            <span className="ed-skeleton" style={{ width: '78%', height: 26, borderRadius: 8 }} />
            <span className="ed-skeleton" style={{ width: '52%', height: 15, borderRadius: 6 }} />
            <span className="ed-skeleton" style={{ width: '44%', height: 15, borderRadius: 6 }} />
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="ed ed--center">
        <p className="ed-error-text">Event not found</p>
        <button type="button" className="ed-back-link" onClick={() => navigate('/')}>Go Home</button>
      </div>
    );
  }

  const invitationText = invitationTextColor(event);

  // One text colour for the whole invitation: header, sections, pill and
  // footer all read it, and the scrims and glass behind them take the opposite
  // tone so it stays legible whether the host picked white, black or a colour.
  const fg = hexToRgb(invitationText.primary) || { r: 255, g: 255, b: 255 };
  const darkText = luminance(invitationText.primary) < 0.4;
  const themeVars = {
    '--ed-fg-rgb': `${fg.r}, ${fg.g}, ${fg.b}`,
    '--ed-inv-rgb': darkText ? '255, 255, 255' : '0, 0, 0',
    '--ed-glass-rgb': darkText ? '248, 248, 250' : '22, 22, 28',
  };
  const font = event.font_family ? getFontById(event.font_family) : null;
  const fontStyle = font && font.id !== 'default' ? { fontFamily: font.fontFamily } : {};

  const allGoingUsers = [
    ...hostProfiles
      .filter((h) => !rsvpAttendees.some((a) => a.user.id === h.id))
      .map((h) => ({ user: h, isHost: h.isMainHost, isCohost: !h.isMainHost })),
    ...rsvpAttendees,
  ];

  const headerProgress = clamp01(scrollY / HEADER_FADE_DISTANCE);
  const titleProgress = titleOffset == null
    ? 0
    : clamp01((scrollY - (titleOffset - TITLE_FADE_DISTANCE)) / TITLE_FADE_DISTANCE);

  // Cover sizing, as the app computes it against the screen width.
  const screenWidth = columnWidth || Math.min(window.innerWidth, 560);
  const coverWidth = screenWidth - 40;
  let coverHeight = coverWidth * 0.6;
  if (coverRatio) {
    const isVertical = coverRatio < 1;
    const maxHeight = isVertical
      ? Math.min(window.innerHeight * 0.6, coverWidth * 1.5)
      : screenWidth * 0.8;
    coverHeight = Math.max(screenWidth * 0.35, Math.min(coverWidth / coverRatio, maxHeight));
  }

  const mediaItems = getMediaItems();
  const spotifyId = event.selected_music_service === 'spotify' ? spotifyPlaylistId(event.playlist) : null;
  const youtubeId = event.selected_music_service === 'youtube' ? youtubePlaylistId(event.youtube_playlist) : null;
  const lowestPrice = isPaidEvent() ? getLowestPassPrice() : null;
  const showHostPill = isHostOrCohost && !isPaidEvent() && !isContributionEvent;
  const showPaidFooter = isPaidEvent() && !isHostOrCohost;
  const showContributionFooter = isContributionEvent && !isHostOrCohost
    && userRsvpStatus === 'going'
    && event.contribution_status === 'requested'
    && event.contribution_selected_users?.includes(user?.id)
    && userPurchase?.verification_status !== 'approved';

  const renderMediaBadge = (item) => {
    if (item?.type === 'pdf') {
      const pages = item.pdfPages?.length || event.pdf_page_images?.length;
      return (
        <>
          <span className="ed-media-badge ed-media-badge--pdf">
            <IoDocumentText size={16} />PDF{pages ? ` • ${pages} pages` : ''}
          </span>
          {pages > 1 && (
            <span className="ed-pdf-pages">
              <IoAlbumsOutline size={16} />Tap to view all pages
            </span>
          )}
        </>
      );
    }
    if (item?.type === 'youtube') {
      return (
        <>
          <span className="ed-media-badge ed-media-badge--youtube"><IoLogoYoutube size={16} />YouTube Video</span>
          <span className="ed-youtube-play"><span><IoPlay size={32} /></span></span>
        </>
      );
    }
    if (item?.type === 'gif') {
      return <span className="ed-media-badge ed-media-badge--gif"><IoPlayCircle size={16} />GIF</span>;
    }
    return null;
  };

  const renderMediaItem = (item, index) => {
    const uri = getMediaDisplayUri(item) || getBackgroundImageUrl();
    const isPlaying = playingYouTube && item?.type === 'youtube' && index === currentMediaIndex;
    const onClick = item?.type === 'pdf'
      ? () => { setShowPdfGallery(true); setPdfCurrentPage(0); }
      : item?.type === 'youtube' ? () => setPlayingYouTube(true) : undefined;

    return (
      <div
        key={`media-${index}`}
        className="ed-cover"
        style={{ width: coverWidth, height: coverHeight }}
      >
        {isPlaying ? (
          <div className="ed-youtube-player">
            <iframe
              src={`https://www.youtube.com/embed/${item.youtubeVideoId || event.youtube_video_id}?autoplay=1&modestbranding=1&rel=0`}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
            <button type="button" className="ed-youtube-close" onClick={() => setPlayingYouTube(false)} aria-label="Close video">
              <IoClose size={24} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="ed-cover-media"
            onClick={onClick}
            disabled={!onClick}
          >
            <img src={uri} alt={`Cover for ${event.title}`} />
            {renderMediaBadge(item)}
          </button>
        )}
      </div>
    );
  };

  // A section someone cannot see yet shows one quiet line in its place —
  // "RSVP to access the photos" — instead of a blurred card over a
  // placeholder. The section's heading already says what is behind it.
  const restrictedLine = (what) => (
    hasPendingRequest ? (
      <div className="ed-locked-row">
        <IoTime size={15} />
        <span>Access requested — waiting on the host</span>
      </div>
    ) : (
      <button type="button" className="ed-locked-row ed-locked-row--button" onClick={requestAccess}>
        <IoLockClosed size={15} />
        <span>RSVP to access {what}</span>
      </button>
    )
  );

  const renderBackground = () => {
    if (event.theme_type === 'video') {
      return (
        <>
          {event.custom_theme_color && <div className="ed-bg-fill" style={{ background: event.custom_theme_color }} />}
          {event.theme_video_cdn_url && (
            <BackgroundVideo src={event.theme_video_cdn_url} />
          )}
        </>
      );
    }
    if (event.theme_image_cdn_url) {
      return (
        <>
          <img className="ed-bg-media" src={event.theme_image_cdn_url} alt="" />
          <div className="ed-bg-gradient ed-bg-gradient--theme" />
        </>
      );
    }
    if (event.custom_theme_color) {
      return <div className="ed-bg-fill" style={{ background: event.custom_theme_color }} />;
    }
    return (
      <>
        <img className="ed-bg-media ed-bg-media--blurred" src={getBackgroundImageUrl()} alt="" />
        <div className="ed-bg-wash" />
        <div className="ed-bg-gradient" />
      </>
    );
  };

  const statusLabel = (status) => ({
    approved: 'Approved ✓',
    pending: 'Pending Review',
    rejected: 'Rejected',
  }[status] || '');

  return (
    <div className="ed" style={themeVars}>
      <div className="ed-background" aria-hidden="true">{renderBackground()}</div>

      {/* Effect: a stacked-alpha MP4 drawn by the shared WebGL player, the
          same page the app shows, in a transparent iframe fixed to the
          viewport. The portrait file on a portrait screen. It never takes a
          click, and its z-index puts it over everything or under the header
          and pill, as the host chose. */}
      {effectHtml && (
        <iframe
          key={effect.id}
          title="Invitation effect"
          srcDoc={effectHtml}
          className={`ed-effect ${event.effect_layer === 'content' ? 'is-content' : 'is-above'}`}
          aria-hidden="true"
          tabIndex={-1}
        />
      )}

      {/* Header: invisible at rest so the controls sit on the invitation, a bar
          once content scrolls underneath. */}
      <header className="ed-header">
        <div className="ed-header-bg" style={{ opacity: headerProgress }} aria-hidden="true">
          <div className="ed-header-hairline" />
        </div>
        <div className="ed-header-content">
          <button type="button" className="ed-header-button" onClick={() => navigate(-1)} aria-label="Go back">
            <IoArrowBack size={24} />
          </button>
          <div className="ed-header-title" aria-hidden="true">
            <span style={{ opacity: 1 - titleProgress }}>Invitation Details</span>
            {titleOffset != null && <span style={{ opacity: titleProgress }}>{event.title}</span>}
          </div>
          <button type="button" className="ed-header-button" onClick={() => setShowShareModal(true)} aria-label="Share invitation">
            <IoShareOutline size={24} />
          </button>
        </div>
      </header>

      <main
        className="ed-column"
        ref={columnRef}
        style={{ paddingBottom: isPaidEvent() ? 140 : 132 }}
      >
        {/* Cover */}
        <div className="ed-cover-wrapper">
          {mediaItems.length > 1 ? (
            <>
              <div
                className="ed-carousel"
                ref={carouselRef}
                style={{ width: coverWidth, height: coverHeight }}
                onScroll={handleCarouselScroll}
              >
                {mediaItems.map(renderMediaItem)}
              </div>
              <div className="ed-pagination">
                {mediaItems.map((_, index) => (
                  <button
                    key={`dot-${index}`}
                    type="button"
                    aria-label={`Show media ${index + 1}`}
                    className={`ed-pagination-dot${index === currentMediaIndex ? ' is-active' : ''}`}
                    onClick={() => carouselRef.current?.scrollTo({ left: index * coverWidth, behavior: 'smooth' })}
                  />
                ))}
              </div>
            </>
          ) : (
            renderMediaItem(mediaItems[0], 0)
          )}
        </div>

        {/* Event info */}
        <section className="ed-section" ref={titleRef}>
          <h1 className="ed-title" style={{ ...fontStyle, color: invitationText.primary }}>{event.title}</h1>

          {event.event_type && (
            <div className="ed-type-tag" style={{ color: invitationText.primary }}>
              <IoPricetag size={14} />
              <span style={fontStyle}>{event.event_type}</span>
            </div>
          )}

          <div className="ed-row" style={{ color: invitationText.primary }}>
            <IoCalendarOutline size={18} />
            <span className="ed-row-text" style={fontStyle}>{formatDateTime()}</span>
          </div>

          {event.location_name && shouldShowLocation() && (
            <div className="ed-row" style={{ color: invitationText.primary }}>
              <IoLocationOutline size={18} />
              <span className="ed-row-text" style={fontStyle}>{event.location_name}</span>
            </div>
          )}

          {event.location_name && !shouldShowLocation() && (
            <div className="ed-row" style={{ color: invitationText.muted }}>
              <IoEyeOffOutline size={18} />
              <span className="ed-row-text ed-row-text--hidden">
                {event.privacy === 'private' ? 'Join guest list to view location' : 'Get approval to view location'}
              </span>
            </div>
          )}

          {event.max_attendees && eventCapacity && (
            <div className={`ed-row ed-spots${eventCapacity.isFull ? ' is-full' : eventCapacity.spotsRemaining <= 3 ? ' is-limited' : ''}`}>
              {eventCapacity.isFull ? <IoAlertCircle size={18} /> : <IoPeopleOutline size={18} />}
              <span className="ed-row-text" style={fontStyle}>
                {eventCapacity.isFull
                  ? `Event Full • ${event.current_spots || 0}/${event.max_attendees}${event.enable_waiting_list ? ' • Waitlist Available' : ''}`
                  : eventCapacity.spotsRemaining <= 3
                    ? `Only ${eventCapacity.spotsRemaining} ${eventCapacity.spotsRemaining === 1 ? 'spot' : 'spots'} left • ${event.current_spots || 0}/${event.max_attendees}`
                    : `${eventCapacity.spotsRemaining} spots available • ${event.current_spots || 0}/${event.max_attendees}`}
              </span>
            </div>
          )}

          {allGoingUsers.length > 0 && (
            canViewRsvpList() ? (
              <button type="button" className="ed-going" onClick={() => setShowGoingModal(true)}>
                {allGoingUsers.slice(0, 5).map((attendee, index) => (
                  <span key={attendee.user.id} className="ed-avatar" style={{ marginLeft: index > 0 ? -10 : 0 }}>
                    <UserAvatar userId={attendee.user.id} user={attendee.user} size={32} />
                    {(attendee.isHost || attendee.isCohost) && (
                      <span className="ed-avatar-badge">
                        {attendee.isHost ? <IoStar size={12} /> : <IoShieldCheckmark size={12} />}
                      </span>
                    )}
                  </span>
                ))}
                {allGoingUsers.length > 5 && (
                  <span className="ed-avatar-more" style={{ marginLeft: -10 }}>+{allGoingUsers.length - 5}</span>
                )}
                <span className="ed-going-text">{allGoingUsers.length === 1 ? ' is' : ' are'} going</span>
                <IoChevronForward size={16} className="ed-going-chevron" />
              </button>
            ) : (
              <div className="ed-locked-row">
                <IoLockClosed size={15} />
                <span>Join to see who's going</span>
              </div>
            )
          )}
        </section>

        {/* Event tools. Each control is gated by role: a guest sees Chat and
            Language, a host the full set. */}
        {user && (
          <div className="ed-tools">
            {isHostOrCohost && (
              <button type="button" className="ed-tool" onClick={() => openAppOnly('blasts')}>
                <span className="ed-tool-icon"><IoMegaphoneOutline size={22} /></span>
                <span className="ed-tool-label">Blasts</span>
              </button>
            )}
            <button type="button" className="ed-tool" onClick={() => openAppOnly('event chat')}>
              <span className="ed-tool-icon"><IoChatbubblesOutline size={22} /></span>
              <span className="ed-tool-label">Chat</span>
            </button>
            <button type="button" className="ed-tool" onClick={() => openAppOnly('translation')}>
              <span className="ed-tool-icon"><IoLanguageOutline size={22} /></span>
              <span className="ed-tool-label">English</span>
            </button>
            {isHostOrCohost && (
              <button type="button" className="ed-tool" onClick={() => openAppOnly('access requests')}>
                <span className="ed-tool-icon"><IoPeopleOutline size={22} /></span>
                <span className="ed-tool-label">{event.privacy === 'private' ? 'Requests' : 'Access'}</span>
              </button>
            )}
          </div>
        )}

        {/* Photo pool */}
        {event.allow_guest_uploads && canAccessContent() && (
          <section className="ed-section">
            <button type="button" className="ed-album" onClick={() => openAppOnly('the photo pool')}>
              <span className="ed-album-icon">
                <IoImages size={28} />
                {event.photos_count > 0 && <span className="ed-count-badge">{event.photos_count}</span>}
              </span>
              <span className="ed-album-info">
                <span className="ed-album-title">Photo Pool</span>
                <span className="ed-album-subtitle">
                  {!event.photos_count
                    ? 'Be the first to share photos!'
                    : `${event.photos_count} photo${event.photos_count !== 1 ? 's' : ''} shared`}
                </span>
              </span>
              <IoChevronForward size={24} className="ed-chevron" />
            </button>
          </section>
        )}

        {event.allow_guest_uploads && !canAccessContent() && (
          <section className="ed-section">
            <div className="ed-album">
              <span className="ed-album-icon is-muted"><IoImages size={28} /></span>
              <span className="ed-album-info">
                <span className="ed-album-title">Event Album</span>
                {restrictedLine('the photos')}
              </span>
            </div>
          </section>
        )}

        {/* Private event, not on the guest list */}
        {user && !isHostOrCohost && event.privacy === 'private' && !onGuestList && (
          <section className="ed-section ed-section--center">
            <span className="ed-status-icon"><IoLockClosed size={48} color="#7C4DFF" /></span>
            <h3 className="ed-status-title">Guest List Only</h3>
            <p className="ed-status-message">
              This is a private event. You need to be on the guest list to view full details and RSVP.
            </p>
            {accessRequest?.status === 'pending' ? (
              <span className="ed-guestlist-status is-pending"><IoTimeOutline size={20} />Request Sent</span>
            ) : accessRequest?.status === 'rejected' ? (
              <span className="ed-guestlist-status is-rejected"><IoCloseCircle size={20} />Request Denied</span>
            ) : (
              <button type="button" className="ed-primary-button" onClick={() => setShowRequestModal(true)}>
                <IoPeople size={20} />Request Guest List Access
              </button>
            )}
          </section>
        )}

        {/* Approval event, access not yet granted */}
        {user && !isHostOrCohost && event.privacy === 'approval' && !canAccessContent() && (
          <section className="ed-section ed-section--center">
            {accessRequest?.status === 'pending' ? (
              <>
                <span className="ed-status-icon"><IoTimeOutline size={48} color="#FFA500" /></span>
                <h3 className="ed-status-title">Request Pending</h3>
                <p className="ed-status-message">
                  Your access request is being reviewed by the host. You'll be notified when they respond.
                </p>
              </>
            ) : accessRequest?.status === 'rejected' ? (
              <>
                <span className="ed-status-icon"><IoCloseCircleOutline size={48} color="#EF4444" /></span>
                <h3 className="ed-status-title" style={{ color: '#EF4444' }}>Request Not Approved</h3>
                <p className="ed-status-message">Your access request was not approved by the host.</p>
              </>
            ) : (
              <>
                <span className="ed-status-icon"><IoLockClosed size={48} color="#7C4DFF" /></span>
                <h3 className="ed-status-title">Approval Required</h3>
                <p className="ed-status-message">
                  This event requires host approval. Request access to view full details and RSVP.
                </p>
                <button type="button" className="ed-primary-button" onClick={() => setShowRequestModal(true)}>
                  <IoKey size={20} />Request Access
                </button>
              </>
            )}
          </section>
        )}

        {/* On the guest list, not yet responded */}
        {user && !isHostOrCohost && event.privacy === 'private' && onGuestList && !userRsvpStatus && (
          <div className="ed-card ed-guestlist-badge">
            <span className="ed-guestlist-badge-icon">🎉</span>
            <span className="ed-guestlist-badge-text">
              <span className="ed-guestlist-badge-title">You're on the Guest List!</span>
              <span className="ed-guestlist-badge-subtitle">RSVP below to access invitation features</span>
            </span>
            <IoArrowDown size={20} className="ed-chevron" />
          </div>
        )}

        {/* Date & time poll */}
        {event.date_time_poll_enabled && !event.date_time_poll_selected && (
          <section className="ed-section ed-poll">
            <div className="ed-poll-header">
              <span className="ed-round-icon"><IoCalendar size={24} /></span>
              <span className="ed-poll-header-text">
                <span className="ed-poll-title">Date & Time - Vote Now!</span>
                <span className="ed-poll-subtitle">Help decide when this event should happen</span>
              </span>
            </div>
            <div className="ed-poll-description">
              <IoInformationCircle size={18} />
              <span>
                The host is letting guests vote on the best date and time. You can select different responses for different dates!
              </span>
            </div>
            {pollResults && (
              <div className="ed-poll-options">
                <span className="ed-poll-options-label">{pollResults.options.length} date options available</span>
                {pollResults.options.slice(0, 3).map((option) => (
                  <div key={option.id} className="ed-poll-option">
                    <IoCalendarOutline size={16} />
                    <span className="ed-poll-option-info">
                      {option.label && <span className="ed-poll-option-label">{option.label}</span>}
                      <span className="ed-poll-option-date">{formatPollOption(option)}</span>
                    </span>
                    {option.goingCount > 0 && <span className="ed-rsvp-badge"><IoCheckmarkCircle size={12} color="#4CD964" />{option.goingCount}</span>}
                    {option.maybeCount > 0 && <span className="ed-rsvp-badge is-maybe"><IoHelpCircle size={12} color="#FF9500" />{option.maybeCount}</span>}
                    {option.notGoingCount > 0 && <span className="ed-rsvp-badge is-no"><IoCloseCircle size={12} color="#FF3B30" />{option.notGoingCount}</span>}
                  </div>
                ))}
                {pollResults.options.length > 3 && (
                  <span className="ed-poll-more">
                    +{pollResults.options.length - 3} more option{pollResults.options.length - 3 !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            )}
          </section>
        )}

        {/* Inline RSVP, for the cases the pinned pill cannot express: a date
            poll, and paid events, which have their own footer. */}
        {!isHostOrCohost && canAccessContent() && !rsvpInFooter && (
          <section className="ed-section ed-rsvp">
            {userRsvpStatus && <p className="ed-rsvp-label">Your response:</p>}
            {event.date_time_poll_enabled && !event.date_time_poll_selected ? (
              <button type="button" className="ed-primary-button ed-primary-button--wide" onClick={() => handleRsvp('going')}>
                <IoCalendar size={20} />Vote on Date & RSVP<IoArrowForward size={20} />
              </button>
            ) : (!eventCapacity?.isFull || userRsvpStatus === 'going') ? (
              <div className="ed-rsvp-circles">
                {[
                  ['going', emojiConfig.going, 'Going'],
                  ...(event.hide_maybe_rsvp ? [] : [['maybe', emojiConfig.maybe, 'Maybe']]),
                  ['not_going', emojiConfig.notGoing, "Can't Go"],
                ].map(([key, emoji, label]) => (
                  <button
                    key={key}
                    type="button"
                    className={`ed-rsvp-circle ed-rsvp-circle--${key}${userRsvpStatus === key ? ' is-selected' : ''}`}
                    onClick={() => handleRsvp(key)}
                    aria-pressed={userRsvpStatus === key}
                  >
                    <span className="ed-rsvp-circle-emoji">{emoji}</span>
                    <span className="ed-rsvp-circle-label">{label}</span>
                  </button>
                ))}
              </div>
            ) : !event.enable_waiting_list ? (
              <div className="ed-full-message">
                <IoCloseCircle size={24} color="#FF3B30" />
                <span>
                  <strong>Event Full</strong>
                  <span>All RSVP spots have been taken for this event.</span>
                </span>
              </div>
            ) : (
              <button type="button" className="ed-primary-button ed-primary-button--wide" onClick={() => openAppOnly('the waitlist')}>
                Join Waitlist
              </button>
            )}
            {rsvpLoading && (
              <div className="ed-rsvp-loading"><span className="ed-spinner" />Updating RSVP...</div>
            )}
          </section>
        )}

        {/* Hosts: waitlist */}
        {isHostOrCohost && event.enable_waiting_list && event.max_attendees && (
          <section className="ed-section">
            <button type="button" className="ed-link-row" onClick={() => openAppOnly('waitlist management')}>
              <IoList size={24} color="#7C4DFF" />
              <span className="ed-section-title">Manage Waitlist</span>
              <IoChevronForward size={20} className="ed-chevron" />
            </button>
          </section>
        )}

        {/* Your pass / contribution */}
        {userPurchase && (!isContributionEvent || userPurchase.payment_status === 'completed') && (
          <div className="ed-purchase">
            <h3 className="ed-section-title">{isContributionEvent ? 'Your Contribution' : 'Your Pass'}</h3>
            <button type="button" className="ed-violet-row" onClick={() => setShowTicketModal(true)}>
              <span className="ed-violet-icon">
                {isContributionEvent ? <IoCash size={24} /> : <IoTicket size={24} />}
              </span>
              <span className="ed-violet-text">
                <span className="ed-violet-title">{isContributionEvent ? 'View Payment Details' : 'View Pass & QR'}</span>
                <span className="ed-violet-subtitle">{statusLabel(userPurchase.verification_status)}</span>
              </span>
              <span className={`ed-status-dot is-${userPurchase.verification_status}`} />
            </button>
          </div>
        )}

        {/* Hosts: payment approvals */}
        {isHostOrCohost && (event.payment_method === 'upi_direct' || isContributionEvent) && (
          <button type="button" className="ed-violet-row ed-violet-row--standalone" onClick={() => openAppOnly('payment approvals')}>
            <span className="ed-violet-icon"><IoCheckmarkCircleOutline size={24} color="#7C4DFF" /></span>
            <span className="ed-violet-text">
              <span className="ed-violet-title">{isContributionEvent ? 'Approve Contributions' : 'Approve Payments'}</span>
              <span className="ed-violet-subtitle">Review and verify UPI payment screenshots</span>
            </span>
            {pendingPaymentCount > 0 && <span className="ed-pending-badge">{pendingPaymentCount}</span>}
            <IoChevronForward size={20} className="ed-chevron" />
          </button>
        )}

        {/* Hosts: contribution setup */}
        {isHostOrCohost && isContributionEvent && (
          <button type="button" className="ed-card ed-plain-row" onClick={() => openAppOnly('contributions')}>
            <span className="ed-violet-icon"><IoWalletOutline size={24} color="#7C4DFF" /></span>
            <span className="ed-violet-text">
              <span className="ed-violet-title">
                {['requested', 'settled'].includes(event.contribution_status) ? 'Contribution Status' : 'Set Contribution Amount'}
              </span>
              <span className="ed-violet-subtitle">
                {event.contribution_status === 'settled'
                  ? 'All payments settled'
                  : event.contribution_status === 'requested'
                    ? `₹${event.contribution_per_head}/person • Track payments`
                    : 'Split costs among attendees'}
              </span>
            </span>
            <IoChevronForward size={20} className="ed-chevron" />
          </button>
        )}

        {/* Hosts */}
        {hostProfiles.length > 0 && (
          <section className="ed-section">
            <h3 className="ed-section-title">
              {linkedCommunity ? 'Community & Hosts' : hostProfiles.length > 1 ? 'Hosts' : 'Host'}
            </h3>

            {linkedCommunity && (
              <>
                <button type="button" className="ed-community" onClick={() => navigate(`/${linkedCommunity.username}`)}>
                  <span className="ed-community-avatar">
                    {linkedCommunity.cover_image_cdn_url
                      ? <img src={linkedCommunity.cover_image_cdn_url} alt="" />
                      : <IoPeople size={24} />}
                  </span>
                  <span className="ed-community-info">
                    <span className="ed-community-name">{linkedCommunity.name}</span>
                    <span className="ed-community-meta">
                      <IoPeopleOutline size={12} />
                      {linkedCommunity.members_count} {linkedCommunity.members_count === 1 ? 'member' : 'members'}
                    </span>
                    <span className="ed-community-badge"><IoBusinessOutline size={10} />Community</span>
                  </span>
                  <IoChevronForward size={20} className="ed-chevron" />
                </button>
                <div className="ed-divider" />
              </>
            )}

            {hostProfiles.map((host) => (
              <button
                key={host.id}
                type="button"
                className="ed-host"
                onClick={() => host.username && navigate(`/${host.username}`)}
              >
                <UserAvatar userId={host.id} user={host} size={50} />
                <span className="ed-host-info">
                  <span className="ed-host-name">{`${host.first_name || ''} ${host.last_name || ''}`.trim()}</span>
                  {host.host_rating_count > 0 && (
                    <span className="ed-host-rating">
                      <IoStar size={12} color="#FFD700" />
                      {Math.round((host.host_rating / 10) * 100)}% recommended as host
                    </span>
                  )}
                  <span className={`ed-host-badge ${host.isMainHost ? 'is-creator' : 'is-cohost'}`}>
                    {host.isMainHost ? 'Creator' : 'Co-Host'}
                  </span>
                </span>
                <IoChevronForward size={20} className="ed-chevron" />
              </button>
            ))}
          </section>
        )}

        {/* About */}
        {event.description && (
          <section className="ed-section">
            <h3 className="ed-section-title" style={{ color: invitationText.primary }}>About</h3>
            <p className="ed-description" style={{ ...fontStyle, color: invitationText.secondary }}>
              {event.description}
            </p>
          </section>
        )}

        {/* Details */}
        {event.custom_fields?.length > 0 && (
          <section className="ed-section ed-section--padded">
            <h3 className="ed-section-title">Details</h3>
            {event.custom_fields.map((field, index) => (
              <div key={index} className="ed-detail">
                <IoInformationCircleOutline size={20} />
                <span className="ed-detail-content">
                  <span className="ed-detail-label" style={fontStyle}>{field.label}</span>
                  <span className="ed-detail-value" style={fontStyle}>{field.value}</span>
                </span>
              </div>
            ))}
          </section>
        )}

        {/* Location */}
        {event.location_name && event.location_lat && event.location_lng && (
          <section className="ed-section">
            <div className="ed-section-header ed-section-header--split ed-section-header--tight">
              <h3 className="ed-section-title">Location</h3>
              <button type="button" className="ed-direction-button" onClick={openDirections} aria-label="Get directions">
                <IoNavigate size={20} />
              </button>
            </div>
            {shouldShowLocation() ? (
              <>
                <div className="ed-location-info">
                  <span className="ed-location-name">{event.location_name}</span>
                  {event.location_address && <span className="ed-location-address">{event.location_address}</span>}
                </div>
                <MapPreview
                  location={{ lat: event.location_lat, lng: event.location_lng }}
                  name={event.title}
                  address={event.location_name}
                  height={200}
                  showDirectionsButton
                />
              </>
            ) : restrictedLine('the location')}
          </section>
        )}

        {/* Games */}
        {event.games_enabled && (
          <section className="ed-section">
            <button type="button" className="ed-link-row" onClick={() => openAppOnly('games')}>
              <IoGameController size={24} color="#7C4DFF" />
              <span className="ed-section-title">Games</span>
              <IoChevronForward size={20} className="ed-chevron" />
            </button>
          </section>
        )}

        {/* Polls */}
        {Array.isArray(event.polls) && event.polls.length > 0 && (
          <section className="ed-section">
            <div className="ed-section-header ed-section-header--split">
              <h3 className="ed-section-title">Polls</h3>
              {isHostOrCohost && (
                <span className="ed-muted">{event.polls.length} poll{event.polls.length !== 1 ? 's' : ''}</span>
              )}
            </div>
            {canAccessContent() ? (
              event.polls.map((poll, index) => (
                <button key={poll.id || index} type="button" className="ed-poll-card" onClick={() => openAppOnly('polls')}>
                  <span className="ed-poll-card-question" style={fontStyle}>{poll.question || poll.title}</span>
                  {(poll.options || []).map((option, i) => (
                    <span key={option.id || i} className="ed-poll-card-option" style={fontStyle}>
                      {option.text || option.label || option}
                    </span>
                  ))}
                </button>
              ))
            ) : restrictedLine('the polls')}
          </section>
        )}

        {/* Spotify playlist */}
        {spotifyId && (
          <section className="ed-section">
            <div className="ed-section-header">
              <IoMusicalNotes size={24} color="#1DB954" />
              <h3 className="ed-section-title" style={fontStyle}>Spotify Playlist</h3>
            </div>
            {canAccessContent() ? (
              <iframe
                className="ed-embed"
                title="Spotify playlist"
                src={`https://open.spotify.com/embed/playlist/${spotifyId}?theme=0`}
                height="380"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
              />
            ) : restrictedLine('the playlist')}
          </section>
        )}

        {/* YouTube playlist */}
        {youtubeId && (
          <section className="ed-section">
            <div className="ed-section-header">
              <IoLogoYoutube size={24} color="#FF0000" />
              <h3 className="ed-section-title" style={fontStyle}>YouTube Playlist</h3>
            </div>
            {canAccessContent() ? (
              <iframe
                className="ed-embed ed-embed--video"
                title="YouTube playlist"
                src={`https://www.youtube.com/embed/videoseries?list=${youtubeId}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            ) : restrictedLine('the playlist')}
          </section>
        )}

        {/* Song requests */}
        {youtubeId && canAccessContent() && (
          <section className="ed-section">
            <div className="ed-section-header">
              <IoMusicalNote size={24} color="#FF0000" />
              <h3 className="ed-section-title" style={fontStyle}>Song Requests</h3>
            </div>
            {isHostOrCohost ? (
              <button type="button" className="ed-primary-button ed-primary-button--wide" onClick={() => setShowSongQueueModal(true)}>
                View Request Queue
              </button>
            ) : (
              <button type="button" className="ed-primary-button ed-primary-button--wide" onClick={() => (user ? setShowSongRequestModal(true) : setShowRsvpSignup(true))}>
                <IoMusicalNote size={20} />Request a Song
              </button>
            )}
          </section>
        )}

        {/* Contact host (paid events) */}
        {isPaidEvent() && event.contact_details && (
          <section className="ed-section ed-section--padded">
            <div className="ed-section-header">
              <IoCallOutline size={24} color="#7C4DFF" />
              <h3 className="ed-section-title">Contact Host</h3>
            </div>
            <div className="ed-contact">
              {event.contact_details.name && (
                <div className="ed-contact-row">
                  <IoPersonOutline size={20} />
                  <span className="ed-contact-label">Name</span>
                  <span className="ed-contact-value">{event.contact_details.name}</span>
                </div>
              )}
              {event.contact_details.email && (
                <a className="ed-contact-row" href={`mailto:${event.contact_details.email}`}>
                  <IoMailOutline size={20} />
                  <span className="ed-contact-label">Email</span>
                  <span className="ed-contact-value">{event.contact_details.email}</span>
                  <IoOpenOutline size={16} />
                </a>
              )}
              {event.contact_details.phone && (
                <a className="ed-contact-row" href={`tel:${event.contact_details.countryCode || '+91'}${event.contact_details.phone}`}>
                  <IoCallOutline size={20} />
                  <span className="ed-contact-label">Phone</span>
                  <span className="ed-contact-value">
                    {event.contact_details.countryCode || '+91'} {event.contact_details.phone}
                  </span>
                  <IoOpenOutline size={16} />
                </a>
              )}
            </div>
          </section>
        )}

        {/* Refund policy (paid events) */}
        {isPaidEvent() && Array.isArray(event.refund_policy) && event.refund_policy.length > 0 && (
          <section className="ed-section ed-section--padded">
            <div className="ed-section-header">
              <IoShieldCheckmarkOutline size={24} color="#7C4DFF" />
              <h3 className="ed-section-title">Refund Policy</h3>
            </div>
            <div className="ed-policy">
              {event.refund_policy.map((policy, index) => (
                <div key={policy.id || index} className="ed-policy-item">
                  <span className="ed-policy-bullet">{index + 1}</span>
                  <span className="ed-policy-text">{policy.text}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Host actions, pinned */}
      {showHostPill && (
        <div className="ed-pill-wrap">
          <div className="ed-pill">
            <button
              type="button"
              className="ed-pill-item"
              onClick={() => navigate(`/edit-event/${event.id}`)}
            >
              <span className="ed-pill-label is-active">Edit</span>
            </button>
            <span className="ed-pill-divider" />
            <button type="button" className="ed-pill-item" onClick={() => setShowShareModal(true)}>
              <span className="ed-pill-label">Share</span>
            </button>
            <span className="ed-pill-divider" />
            <button
              type="button"
              className="ed-pill-item"
              onClick={() => navigate(`/upgrade/tiers?eventId=${event.id}&eventName=${encodeURIComponent(event.title)}`)}
            >
              <span className="ed-pill-label">Upgrade</span>
            </button>
          </div>
        </div>
      )}

      {/* RSVP, pinned */}
      {rsvpInFooter && (
        <div className="ed-pill-wrap">
          <div className="ed-pill">
            {rsvpFailed ? (
              <button
                type="button"
                className="ed-pill-item"
                onClick={() => { setRsvpFailed(false); handleRsvp(userRsvpStatus ?? 'going'); }}
              >
                <IoAlertCircleOutline size={16} color="#FF3B30" />
                <span className="ed-pill-label is-danger">Couldn't save · Retry</span>
              </button>
            ) : ctaReplacesRsvpButtons(footerCta) ? (
              <button
                type="button"
                className="ed-pill-item"
                disabled={!!footerCta.disabled}
                onClick={() => handleFooterCta(footerCta.kind)}
                title={footerCta.hint ?? undefined}
              >
                <span className={`ed-pill-label${footerCta.disabled ? '' : ' is-active'}`}>{footerCta.label}</span>
              </button>
            ) : (
              ['going', 'maybe', 'not_going'].map((key, i) => {
                const label = key === 'going' ? 'Going' : key === 'maybe' ? 'Maybe' : "Can't go";
                const emoji = key === 'going' ? emojiConfig.going : key === 'maybe' ? emojiConfig.maybe : emojiConfig.notGoing;
                const active = userRsvpStatus === key;
                return (
                  <React.Fragment key={key}>
                    {i > 0 && <span className="ed-pill-divider" />}
                    <button
                      type="button"
                      className="ed-pill-item"
                      onClick={() => handleRsvp(key)}
                      aria-pressed={active}
                    >
                      {emoji && <span className="ed-pill-emoji">{emoji}</span>}
                      <span className={`ed-pill-label${active ? ' is-active' : ''}`}>{label}</span>
                    </button>
                  </React.Fragment>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Paid events, pinned */}
      {showPaidFooter && (
        <div className="ed-footer">
          <div className="ed-footer-content">
            <div className="ed-price">
              <span className="ed-price-label">From</span>
              <span className="ed-price-amount">₹{lowestPrice}</span>
              <span className="ed-price-label">onwards</span>
            </div>
            <button type="button" className="ed-buy-button" onClick={() => setShowPassModal(true)}>
              Buy Passes<IoArrowForward size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Contribution, pinned */}
      {showContributionFooter && (
        <div className="ed-footer">
          <div className="ed-footer-content">
            {userPurchase?.payment_status === 'completed' ? (
              <>
                <div className="ed-contribution">
                  <span className="ed-contribution-label">Contribution Paid</span>
                  <span className="ed-contribution-amount" style={{ fontSize: 16 }}>Pending host approval</span>
                </div>
                <button type="button" className="ed-buy-button is-success" onClick={() => setShowTicketModal(true)}>
                  View Details
                </button>
              </>
            ) : (
              <>
                <div className="ed-contribution">
                  <span className="ed-contribution-label">Your Share</span>
                  <span className="ed-contribution-amount">
                    ₹{event.contribution_per_head?.toLocaleString('en-IN')}{' '}
                    <span className="ed-contribution-suffix">contribution</span>
                  </span>
                </div>
                <button type="button" className="ed-buy-button" onClick={() => openAppOnly('contribution payments')}>
                  Pay Now<IoArrowForward size={20} />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      {showRsvpSignup && (
        <GuestlistRsvpModal
          onClose={() => { setShowRsvpSignup(false); setPendingRsvpOnAuth(null); }}
          onAuthenticated={handleAuthSuccess}
          eventTitle={event.title}
        />
      )}

      {showRequestModal && user && (
        <RequestAccessModal
          eventId={event.id}
          financialYear={event.financial_year}
          eventTitle={event.title}
          onClose={() => setShowRequestModal(false)}
          onRequested={() => {
            setShowRequestModal(false);
            checkUserAccess(event, user.id);
          }}
        />
      )}

      {showPassModal && (
        <PassPurchaseModal
          passes={event.passes}
          eventTitle={event.title}
          onClose={() => setShowPassModal(false)}
          onPurchase={() => openAppOnly('pass purchases')}
        />
      )}

      {showGoingModal && canViewRsvpList() && (
        <GoingModal
          goingUsers={allGoingUsers}
          canExport={isHostOrCohost}
          event={event}
          onClose={() => setShowGoingModal(false)}
          onOpenProfile={(username) => {
            setShowGoingModal(false);
            if (username) navigate(`/${username}`);
          }}
        />
      )}

      <AnimatePresence>
        {showTicketModal && userPurchase && (
          <SheetModal onClose={() => setShowTicketModal(false)} className="ed-ticket-modal">
            <h2>{isContributionEvent ? 'Your Contribution' : 'Your Pass'}</h2>
            <div className="ed-ticket-rows">
              {[
                ['Pass Type', userPurchase.pass_name || 'General Admission'],
                userPurchase.quantity > 1 && ['Quantity', `${userPurchase.quantity} passes`],
                userPurchase.amount != null && ['Amount Paid', `₹${userPurchase.amount}`],
                ['Status', statusLabel(userPurchase.verification_status)],
                userPurchase.transaction_id && ['Transaction ID', userPurchase.transaction_id],
                ['Purchased', new Date(userPurchase.created_at).toLocaleDateString(undefined, {
                  month: 'short', day: 'numeric', year: 'numeric',
                })],
              ].filter(Boolean).map(([label, value]) => (
                <div key={label} className="ed-ticket-row">
                  <span>{label}</span>
                  <span>{value}</span>
                </div>
              ))}
            </div>
            {userPurchase.qr_code && (
              <div className="ed-ticket-qr">
                <img src={userPurchase.qr_code} alt="Pass QR code" />
                <p>Show this QR code at entry</p>
              </div>
            )}
          </SheetModal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAppDownloadModal && (
          <SheetModal onClose={() => setShowAppDownloadModal(false)} className="ed-app-modal">
            <span className="ed-app-modal-icon"><IoPhonePortraitOutline size={40} /></span>
            <h2>Open in the Invits app</h2>
            <p>
              {appDownloadAction
                ? <>To use <strong>{appDownloadAction}</strong>, download the Invits app.</>
                : 'Download the Invits app to do more with this invitation.'}
            </p>
            <button type="button" className="ed-primary-button ed-primary-button--wide" onClick={() => { window.location.href = 'https://invits.co/app'; }}>
              <IoDownloadOutline size={20} />Download Invits
            </button>
            <span className="ed-muted">Available on iOS and Android</span>
          </SheetModal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showShareModal && (
          <SheetModal onClose={() => setShowShareModal(false)} className="ed-share-modal">
            <h2>Share Invitation</h2>
            <p>{event.title}</p>
            <div className="ed-share-url">
              <input type="text" value={shareUrl} readOnly onClick={(e) => e.target.select()} aria-label="Invitation link" />
              <button type="button" onClick={handleCopyShareUrl}>
                {copied ? <IoCheckmark size={18} /> : <IoCopyOutline size={18} />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            {navigator.share && (
              <button
                type="button"
                className="ed-primary-button ed-primary-button--wide"
                onClick={() => navigator.share({ title: event.title, url: shareUrl }).catch(() => {})}
              >
                <IoShareOutline size={20} />Share via…
              </button>
            )}
          </SheetModal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPdfGallery && (() => {
          const pdfPages = mediaItems.find((m) => m.type === 'pdf')?.pdfPages || event.pdf_page_images || [];
          return (
            <motion.div
              className="ed-pdf-gallery"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="ed-pdf-gallery-header">
                <button type="button" className="ed-header-button" onClick={() => setShowPdfGallery(false)} aria-label="Close">
                  <IoClose size={28} />
                </button>
                <span className="ed-pdf-gallery-title">{event.title || 'Event Invitation'}</span>
                <span style={{ width: 40 }} />
              </div>
              <div
                className="ed-pdf-gallery-pages"
                onScroll={(e) => {
                  const { scrollLeft, clientWidth } = e.currentTarget;
                  if (clientWidth) setPdfCurrentPage(Math.round(scrollLeft / clientWidth));
                }}
              >
                {pdfPages.map((page, index) => (
                  <div key={`pdf-page-${page.page ?? index}`} className="ed-pdf-page">
                    <img src={page.cdn_url} alt={`Page ${page.page ?? index + 1}`} />
                    <span className="ed-pdf-page-number">Page {page.page ?? index + 1}</span>
                  </div>
                ))}
              </div>
              {pdfPages.length > 1 && (
                <span className="ed-pdf-indicator">{pdfCurrentPage + 1} / {pdfPages.length}</span>
              )}
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {showDateTimePollModal && pollResults && (
        <DateTimePollModal
          pollOptions={pollResults.options}
          onClose={() => setShowDateTimePollModal(false)}
          onVote={handlePollVote}
          isVoting={isVotingOnPoll}
        />
      )}

      {showSongRequestModal && (
        <SongRequestModal
          onClose={() => setShowSongRequestModal(false)}
          onSubmitRequest={handleSongRequest}
          isSubmitting={false}
          eventDetails={event}
        />
      )}

      {showSongQueueModal && (
        <SongQueueModal
          onClose={() => setShowSongQueueModal(false)}
          eventId={event.id}
          financialYear={event.financial_year}
        />
      )}

      {showAdditionalQuestionsModal && event.additional_info_fields && (
        <AdditionalQuestionsModal
          questions={event.additional_info_fields}
          onClose={() => { setShowAdditionalQuestionsModal(false); setPendingRsvpStatus(null); }}
          onSubmit={handleAdditionalQuestionsSubmit}
          isSubmitting={false}
        />
      )}

      {showPlusOnesModal && (
        <PlusOnesModal
          maxPlusOnes={event.plus_ones_allowed}
          eventTitle={event.title}
          onSubmit={handlePlusOnesSubmit}
          onSkip={() => handlePlusOnesSubmit([])}
          onClose={() => { setShowPlusOnesModal(false); setPendingRsvpStatus(null); }}
        />
      )}
    </div>
  );
};

/**
 * The theme video, playing silently on a loop with no controls.
 *
 * React sets `muted` as a property rather than an attribute, and mobile
 * browsers only autoplay video they can see is muted — so it is forced here,
 * and play() is retried when the tab returns or the guest first touches the
 * page (Low Power Mode on iOS refuses autoplay until then).
 */
const BackgroundVideo = ({ src }) => {
  const ref = useRef(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return undefined;
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    const play = () => {
      if (video.paused) video.play().catch(() => {});
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') play();
    };

    play();
    video.addEventListener('canplay', play);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('touchstart', play, { passive: true });
    window.addEventListener('click', play);
    return () => {
      video.removeEventListener('canplay', play);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('touchstart', play);
      window.removeEventListener('click', play);
    };
  }, [src]);

  return (
    <video
      ref={ref}
      className="ed-bg-media ed-bg-video"
      src={src}
      autoPlay
      loop
      muted
      playsInline
      preload="auto"
      controls={false}
      disablePictureInPicture
      disableRemotePlayback
      tabIndex={-1}
    />
  );
};

/** A bottom sheet on phones, a centred dialog on wider screens. */
const SheetModal = ({ onClose, className = '', children }) => (
  <motion.div
    className="ed-modal-overlay"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onClose}
  >
    <motion.div
      className={`ed-modal ${className}`}
      role="dialog"
      aria-modal="true"
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 40, opacity: 0 }}
      onClick={(e) => e.stopPropagation()}
    >
      <button type="button" className="ed-modal-close" onClick={onClose} aria-label="Close">
        <IoClose size={22} />
      </button>
      {children}
    </motion.div>
  </motion.div>
);

const csvCell = (v) => {
  if (v == null) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const escapeHtml = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[c]));

/** Who's going, with CSV / PDF export for hosts. */
const GoingModal = ({ goingUsers, canExport, event, onClose, onOpenProfile }) => {
  const fetchAllRsvps = async () => {
    const { data, error } = await supabase
      .from('event_rsvps')
      .select(`
        status, plus_ones, plus_one_names, additional_info_responses, created_at,
        user:users!event_rsvps_user_id_fkey(id, first_name, last_name, username, phone_number)
      `)
      .eq('event_id', event.id)
      .order('status')
      .order('created_at');
    if (error) throw error;
    return (data || []).filter((r) => r.user);
  };

  const exportCsv = async () => {
    try {
      const rows = await fetchAllRsvps();
      const infoKeys = [...new Set(rows.flatMap((r) => Object.keys(r.additional_info_responses || {})))];
      const lines = [['Name', 'Username', 'Phone', 'Status', 'Plus Ones Count', 'Plus One Names', ...infoKeys].map(csvCell).join(',')];
      rows.forEach((r) => {
        lines.push([
          `${r.user.first_name || ''} ${r.user.last_name || ''}`.trim(),
          r.user.username || '', r.user.phone_number || '', r.status, r.plus_ones || 0,
          (r.plus_one_names || []).join('; '),
          ...infoKeys.map((k) => (r.additional_info_responses || {})[k] || ''),
        ].map(csvCell).join(','));
      });
      const blob = new Blob([`﻿${lines.join('\n')}`], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${event.title.replace(/[^a-z0-9]/gi, '_')}_rsvp_list.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export CSV error:', err);
      alert('Failed to export. Please try again.');
    }
  };

  const exportPdf = async () => {
    try {
      const rows = await fetchAllRsvps();
      const label = { going: 'Going', maybe: 'Maybe', not_going: 'Not Going' };
      const count = (s) => rows.filter((r) => r.status === s).length;
      const body = rows.map((r, i) => `<tr>
        <td>${i + 1}</td>
        <td>${escapeHtml(`${r.user.first_name || ''} ${r.user.last_name || ''}`.trim() || '—')}</td>
        <td>@${escapeHtml(r.user.username || '—')}</td>
        <td>${label[r.status] || escapeHtml(r.status)}</td>
        <td>${r.plus_ones ? `+${r.plus_ones} ${escapeHtml((r.plus_one_names || []).join(', '))}` : '—'}</td>
      </tr>`).join('');
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(event.title)} — RSVP List</title>
        <style>
          body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#1a1a2e;padding:32px}
          h1{font-size:24px;margin:0 0 4px}p{color:#888;margin:0 0 20px;font-size:13px}
          table{width:100%;border-collapse:collapse;font-size:13px}
          th{background:#f3eeff;color:#5a2db8;text-align:left;padding:8px 12px;font-size:11px;text-transform:uppercase}
          td{padding:8px 12px;border-bottom:1px solid #eee}
        </style></head><body>
        <h1>${escapeHtml(event.title)}</h1>
        <p>${count('going')} going · ${count('maybe')} maybe · ${count('not_going')} not going</p>
        <table><thead><tr><th>#</th><th>Name</th><th>Username</th><th>Status</th><th>Plus ones</th></tr></thead>
        <tbody>${body}</tbody></table></body></html>`;
      const w = window.open('', '_blank', 'width=900,height=700');
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 400);
    } catch (err) {
      console.error('Export PDF error:', err);
      alert('Failed to export. Please try again.');
    }
  };

  return (
    <SheetModal onClose={onClose} className="ed-going-modal">
      <h2>{goingUsers.length} Going</h2>
      {canExport && (
        <div className="ed-going-export">
          <button type="button" onClick={exportCsv}><IoDownloadOutline size={16} />Excel</button>
          <button type="button" onClick={exportPdf}><IoDocumentTextOutline size={16} />PDF</button>
        </div>
      )}
      <div className="ed-going-list">
        {goingUsers.map((attendee) => {
          const plusOneNames = attendee.plus_one_names || [];
          const plusOneCount = plusOneNames.length || attendee.plus_ones || 0;
          return (
            <div key={attendee.user.id} className="ed-going-item">
              <button type="button" className="ed-going-user" onClick={() => onOpenProfile(attendee.user.username)}>
                <UserAvatar userId={attendee.user.id} user={attendee.user} size={48} />
                <span className="ed-going-user-info">
                  <span className="ed-host-name">{`${attendee.user.first_name || ''} ${attendee.user.last_name || ''}`.trim()}</span>
                  <span className="ed-going-tags">
                    {(attendee.isHost || attendee.isCohost) && (
                      <span className={`ed-host-badge ${attendee.isHost ? 'is-creator' : 'is-cohost'}`}>
                        {attendee.isHost ? 'Host' : 'Co-host'}
                      </span>
                    )}
                    {plusOneCount > 0 && (
                      <span className="ed-host-badge is-cohost">+{plusOneCount} plus one{plusOneCount > 1 ? 's' : ''}</span>
                    )}
                  </span>
                </span>
              </button>
              {canExport && plusOneNames.length > 0 && (
                <p className="ed-going-plus-ones">Plus ones: {plusOneNames.join(', ')}</p>
              )}
            </div>
          );
        })}
      </div>
    </SheetModal>
  );
};

export default EventInvitation;
